import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/lang";

const Navbar = () => {
  const { lang, setLang, t, scrollTo } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => scrollY.on("change", (v) => setScrolled(v > 40)), [scrollY]);

  const links = [
    { id: "#services", label: t.nav.services, testid: "nav-link-services" },
    { id: "#methode", label: t.nav.method, testid: "nav-link-method" },
    { id: "#chiffres", label: t.nav.stats, testid: "nav-link-stats" },
    { id: "#contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const go = (id) => {
    setOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => scrollTo(id), 450);
      return;
    }
    scrollTo(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        solid || open
          ? "border-b border-slate-200/70 bg-white/90 shadow-[0_8px_30px_rgba(11,25,44,0.06)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => go(0)}
          className="flex items-center rounded-lg bg-white px-4 py-2 shadow-sm transition-transform duration-300 hover:scale-[1.03]"
          aria-label="BEINTEC"
        >
          <img src="/assets/beintec-logo-mark-hd.png" alt="BEINTEC" className="h-9 w-auto sm:h-10" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className={`group relative text-sm font-medium transition-colors duration-300 ${
                solid ? "text-slate-600 hover:text-bei-brand" : "text-slate-200 hover:text-white"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-bei-cyan transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div
            data-testid="language-switcher"
            className={`flex items-center rounded-full border p-0.5 font-mono text-xs font-semibold ${
              solid ? "border-slate-300" : "border-white/25"
            }`}
          >
            {["fr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`rounded-full px-3 py-1 uppercase transition-colors duration-300 ${
                  lang === l
                    ? "bg-bei-brand text-white"
                    : scrolled
                      ? "text-slate-500 hover:text-bei-brand"
                      : "text-slate-300 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-cta-audit"
            onClick={() => go("#contact")}
            className="group flex items-center gap-2 rounded-full bg-bei-brand px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-bei-azure hover:shadow-[0_8px_24px_rgba(0,82,204,0.35)]"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`lg:hidden ${solid || open ? "text-slate-900" : "text-white"}`}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-200/70 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-slate-700 transition-colors hover:bg-bei-mist hover:text-bei-brand"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center rounded-full border border-slate-300 p-0.5 font-mono text-xs font-semibold">
                  {["fr", "en"].map((l) => (
                    <button
                      key={l}
                      data-testid={`mobile-lang-${l}`}
                      onClick={() => setLang(l)}
                      className={`rounded-full px-3 py-1 uppercase ${
                        lang === l ? "bg-bei-brand text-white" : "text-slate-500"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="mobile-nav-cta-audit"
                  onClick={() => go("#contact")}
                  className="rounded-full bg-bei-brand px-5 py-2.5 text-sm font-semibold text-white"
                >
                  {t.nav.cta}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
